import React from 'react';
import useSwr from 'swr';
import { useRouter } from 'next/router';
import orderBy from 'lodash/orderBy';
import { styled } from '@mui/material/styles';
import Container, { ContainerProps } from '@mui/material/Container';
import Table from '@mui/material/Table';
import Divider from '@mui/material/Divider';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MuiPaper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';

import Filters from '../Filters';
import { Option } from '../Filter';
import Pagination from '../Pagination';
import { useLocalizationContext } from '../LocalizationContext';
import usePrevious from '../../utils/usePrevious';
import { sidekick } from '../../utils/sidekick';

export interface LeverProps {
  id?: string;
  sidekickLookup: any;
  settings?: {
    url?: string;
  };
}

interface Items {
  [x: string]: any;
}

interface Position {
  id: string;
  text: string;
  hostedUrl: string;
  categories: {
    location: string;
    department: string;
  };
}

interface Positions {
  department: string;
  positions: Position[];
}

interface LeverItem {
  applyUrl: string;
  hostedUrl: string;
  categories: {
    location: string;
    department: string;
  };
}

interface OptionsObj {
  [key: string]: Option;
}

interface Query {
  [key: string]: string | string[] | undefined;
}

const ITEMS_PER_PAGE = 20;

export const Lever = ({ id, sidekickLookup, settings }: LeverProps) => {
  const [filter, setFilter] = React.useState<any>({});
  const [items, setItems] = React.useState<Positions[]>([]);
  const [departments, setDepartments] = React.useState<Option[]>([]);
  const [locations, setLocations] = React.useState<Option[]>([]);
  const [total, setTotal] = React.useState(0);
  const router = useRouter();
  const localization = useLocalizationContext();
  const { page = 1, slug: _slug, ...query } = router.query;
  const prevQuery: Query = usePrevious<Query>(query);
  const url = settings?.url;

  const fetchItems = async (url: string): Promise<LeverItem[]> => {
    const res = await fetch(url);
    const results: LeverItem[] = await res.json();
    const ordered = orderBy(results, ['categories.department']);
    return ordered;
  };

  const { data } = useSwr(!!url ? [url] : null, fetchItems);

  React.useEffect(() => {
    if (!data) return;
    const items: Items = {};

    const { department, location } = filter;
    let deptFilters: string[] = [];
    let locFilters: string[] = [];
    if (department && typeof department === 'string') deptFilters.push(department);
    else if (Array.isArray(department)) deptFilters = department.map((item) => item);

    if (location && typeof location === 'string') locFilters.push(location);
    else if (Array.isArray(location)) locFilters = location.map((item) => item);

    let filteredResults = [...data];

    if (!!deptFilters.length)
      filteredResults = filteredResults.filter((item) => deptFilters.includes(item.categories.department));

    if (!!locFilters.length)
      filteredResults = filteredResults.filter((item) => locFilters.includes(item.categories.location));

    setTotal(Math.ceil((filteredResults?.length ?? 0) / ITEMS_PER_PAGE));

    const paginatedResults = filteredResults.slice(ITEMS_PER_PAGE * (Number(page) - 1), ITEMS_PER_PAGE * Number(page));

    paginatedResults.forEach((item) => {
      if (!items[item.categories.department]) items[item.categories.department] = [item];
      else items[item.categories.department].push(item);
    });

    setItems(Object.keys(items).map((key) => ({ department: key, positions: items[key] })));
  }, [data, page, filter]);

  React.useEffect(() => {
    if (!data) return;
    const depts: OptionsObj = {};
    const locs: OptionsObj = {};
    data.forEach((item) => {
      if (!depts[item.categories.department])
        depts[item.categories.department] = {
          label: item.categories.department,
          value: item.categories.department,
        };
      if (!locs[item.categories.location])
        locs[item.categories.location] = {
          label: item.categories.location,
          value: item.categories.location,
        };
    });

    setDepartments(Object.keys(depts).map((key) => depts[key]));
    setLocations(Object.keys(locs).map((key) => locs[key]));
  }, [data]);

  React.useEffect(() => {
    if (JSON.stringify(prevQuery ?? {}) !== JSON.stringify(query)) {
      setFilter(query);
    }
  }, [prevQuery, query]);

  return (
    <ErrorBoundary>
      <Root elevation={3} maxWidth="xl" {...sidekick(sidekickLookup)} id={id}>
        <Typography
          variant="h2"
          color="primary"
          mb={[5, 10]}
          sx={{
            fontSize: 44,
            textAlign: 'center',
          }}
        >
          {localization['careers.openPositionsLabel']?.shortTextValue ?? 'Open Positions'}
        </Typography>
        <Paper elevation={3}>
          <FilterContainer>
            <Filters
              filters={[
                {
                  id: 'department',
                  items: departments,
                  label: localization['careers.filtersTeamsLabel']?.shortTextValue ?? 'teams',
                  variant: 'outlined',
                  multiple: true,
                },
                {
                  id: 'location',
                  items: locations,
                  label: localization['careers.filtersLocationLabel']?.shortTextValue ?? 'location',
                  variant: 'outlined',
                  multiple: true,
                },
              ]}
            />
          </FilterContainer>
          <ContainerTable>
            <Table aria-label="simple table">
              {items?.map((row) => (
                <BoxContainer>
                  <TableHead sx={{ mb: 2 }}>
                    <DepartmentRow>
                      <Departament>{row.department}</Departament>
                    </DepartmentRow>
                  </TableHead>
                  {row.positions.map((item) => (
                    <>
                      <Link href={item.hostedUrl} underline="none" target="_blank">
                        <TableRowContainer
                          key={item.id}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#F9F9F9',
                              color: 'secondary.contrastText',
                            },
                          }}
                        >
                          <JobPosition component="th" scope="row">
                            {item.text}
                            <Location>{item.categories.location}</Location>
                          </JobPosition>
                        </TableRowContainer>
                      </Link>
                      <Divider sx={{ mx: [2, 10] }} />
                    </>
                  ))}
                </BoxContainer>
              ))}
            </Table>
          </ContainerTable>
          <TableFooterContainer>
            <FooterRow sx={{ my: [3, 4] }}>
              <Pagination count={total} />
            </FooterRow>
          </TableFooterContainer>
          {/* MOBILE FOOTER */}
          <MobileTableFooterContainer sx={{ display: 'flex' }}>
            <FooterRow>
              <Pagination count={total} />
            </FooterRow>
          </MobileTableFooterContainer>
        </Paper>
      </Root>
    </ErrorBoundary>
  );
};

const Root = styled(Container)<ContainerProps<React.ElementType>>(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    padding: 0,
  },
}));

const Paper = styled(MuiPaper)<ContainerProps<React.ElementType>>(({ theme }) => ({
  margin: '0 auto',
  '& [class*="Filter-root"]': {
    paddingTop: theme.spacing(2),
    marginBottom: 0,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 4),
      paddingTop: theme.spacing(3),
    },
  },
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 10),
  paddingTop: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0),
  },
}));

const ContainerTable = styled(TableContainer)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(5),
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(2),
  },
  border: 0,
  boxShadow: 'none',
}));

const BoxContainer = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const DepartmentRow = styled(TableRow)(() => ({
  overflowWrap: 'break-word',
}));

const TableRowContainer = styled(TableRow)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflowWrap: 'break-word',
}));

const Departament = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0, 10),
  paddingTop: theme.spacing(5),
  marginBottom: theme.spacing(-2),

  fontSize: 36,
  border: 'none',

  lineHeight: '132%',
  [theme.breakpoints.down('md')]: {
    fontSize: 30,
    padding: theme.spacing(0, 4),
    paddingTop: theme.spacing(2),
  },
}));

const JobPosition = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(3, 10),
  color: theme.palette.secondary.contrastText,
  border: 'none',
  fontSize: 28,
  fontWeight: 300,
  lineHeight: '94%',
  '&:hover': {
    fontWeight: 700,
  },
}));

const Location = styled(Typography)(({ theme }) => ({
  padding: 0,
  paddingTop: theme.spacing(2),
  border: 'none',
  fontSize: 22,
  lineHeight: '94%',
  fontWeight: 300,
}));

const TableFooterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  paddingBottom: theme.spacing(5),

  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const MobileTableFooterContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  paddingBottom: theme.spacing(2),

  justifyContent: 'space-between',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'block',
  },
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const FooterRow = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

export default Lever;
